import { isNil, some } from 'lodash';
import { createSelector, type Selector } from 'reselect';
import type { BoundEnabledPerspective } from 'configuration-schemas/bindings.types';
import type { AppContext } from 'configuration-schemas/confdefn';
import type { AppState } from 'src/reducers';
import { getPerspectiveLookupKey } from 'src/services/configuration/service.utils';

const getAppContextConfig: Selector<AppState, AppContext> = (state) => {
  return state.appConfig.tenantConfig.context;
};

const getActiveTab: Selector<AppState, string> = (state) => {
  return state.perspective.activeTab.toLowerCase().replace('-', '');
};

export const getActivePerspective: Selector<AppState, BoundEnabledPerspective | null> = (state) => {
  return state.perspective.selected;
};

export const getPerspectiveCanScopeWithFloorsets: Selector<AppState, boolean> = (state) => {
  return state.perspective.selected?.canScopeWithFloorsets ?? false;
};

const getPerspectiveHeaderbarType = (state: AppState) => {
  return state.appConfig.tenantConfig.perspective?.headerbar.type;
};

export const hasContextsWithPlanTypeAllocation = createSelector(getAppContextConfig, (appContext) => {
  return some(appContext, (context) => context.planType === 'allocation');
});

const getActiveTabContextIndex = createSelector(getAppContextConfig, getActiveTab, (appContext, activeTab) => {
  // find the index of the context that contains the activeTab
  return Object.values(appContext)
    .map((ctx) => ctx.tabs.map((tab) => tab.toLowerCase()))
    .findIndex((x) => x.indexOf(activeTab) > -1);
});

export const getActiveContext = createSelector(
  [getAppContextConfig, getActiveTabContextIndex],
  (appContext, activeContextIndex) => {
    return activeContextIndex > -1 ? Object.values(appContext)[activeContextIndex] : undefined;
  },
);

export const getActiveContextString = createSelector(
  [getAppContextConfig, getActiveTabContextIndex],
  (appContext, activeContextIndex) => {
    const contextKeys = Object.keys(appContext);
    if (activeContextIndex < 0 || activeContextIndex >= contextKeys.length) {
      return '';
    }
    const key = contextKeys[activeContextIndex];
    const context = appContext[key];

    // return the alternateContextKey if present; otherwise fall back to the original key
    return context.alternateContextKey ?? key;
  },
);

export const getActiveContextPlanType = createSelector(
  [getAppContextConfig, getActiveTabContextIndex],
  (appContext, activeContextIndex) => {
    const contextKeys = Object.keys(appContext);
    if (activeContextIndex < 0 || activeContextIndex >= contextKeys.length) {
      // default to previous behavior
      return 'assortment';
    }
    const key = contextKeys[activeContextIndex];
    const context = appContext[key];

    return context.planType ?? 'assortment';
  },
);

export const getActivePerspectiveEditStyleButtonConfig = createSelector(
  [getActivePerspective, getActiveContext],
  (activePerspective, activeContext) => {
    if (isNil(activePerspective) || isNil(activeContext)) {
      return undefined;
    }

    // only return the config if the button is enabled (by configuration) in the active perspective
    const perspectiveLookupKey = getPerspectiveLookupKey(activePerspective);
    const enabledInPerspective = isNil(activeContext?.editStyleBtn)
      ? undefined
      : activeContext.editStyleBtn.inPerspectives.includes(perspectiveLookupKey);
    return enabledInPerspective ? activeContext?.editStyleBtn : undefined;
  },
);

export const getActivePerspectiveCartButtonConfig = createSelector(
  [getActivePerspective, getActiveContext],
  (activePerspective, activeContext) => {
    if (isNil(activePerspective) || isNil(activeContext)) {
      return undefined;
    }

    // only return the config if the button is enabled (by configuration) in the active perspective
    const perspectiveLookupKey = getPerspectiveLookupKey(activePerspective);
    const enabledInPerspective = isNil(activeContext?.cartBtn)
      ? undefined
      : activeContext.cartBtn.inPerspectives.includes(perspectiveLookupKey);
    return enabledInPerspective ? activeContext?.cartBtn : undefined;
  },
);

export const getActivePerspectiveQuickReconButtonConfig = createSelector(
  [getActivePerspective, getActiveContext],
  (activePerspective, activeContext) => {
    if (isNil(activePerspective) || isNil(activeContext)) {
      return undefined;
    }

    // only return the config if the button is enabled (by configuration) in the active perspective
    const perspectiveLookupKey = getPerspectiveLookupKey(activePerspective);
    const enabledInPerspective = isNil(activeContext?.quickReconBtn)
      ? undefined
      : activeContext.quickReconBtn.inPerspectives.includes(perspectiveLookupKey);
    return enabledInPerspective ? activeContext?.quickReconBtn : undefined;
  },
);

export const getActivePerspectiveAgentButtonConfig = createSelector(
  [getActivePerspective, getActiveContext],
  (activePerspective, activeContext) => {
    if (isNil(activePerspective) || isNil(activeContext)) {
      return undefined;
    }

    // only return the config if the button is enabled (by configuration) in the active perspective
    const perspectiveLookupKey = getPerspectiveLookupKey(activePerspective);
    const enabledInPerspective = isNil(activeContext?.agentBtn)
      ? undefined
      : activeContext.agentBtn.inPerspectives.includes(perspectiveLookupKey);
    return enabledInPerspective ? activeContext?.agentBtn : undefined;
  },
);

export const getActivePerspectiveWorklistConfigButtonsConfig = createSelector(
  [getActivePerspective, getActiveContext],
  (activePerspective, activeContext) => {
    if (isNil(activePerspective) || isNil(activeContext)) {
      return undefined;
    }
    return activeContext?.worklistConfigButtons ?? undefined;
  },
);

export const getFiltersEnabled = createSelector(
  [getActiveContext, getPerspectiveHeaderbarType],
  (activeContext, headerbarType) => {
    let isEnabled = true;
    const filtersDisabledInContext = activeContext?.filters?.disabled;

    if (headerbarType === 'MfpHeaderbar' || filtersDisabledInContext || isNil(activeContext)) {
      isEnabled = false;
    }

    return isEnabled;
  },
);
